import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db7lnuf3hiaolq192j2g";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后重试"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-700 bg-background-950 px-3 py-2 text-sm text-foreground-100 placeholder:text-foreground-500 outline-none transition-colors focus:border-primary-500";

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-4"
    >
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-guard-offscreen"
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-xs text-foreground-400">
            你的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入称呼"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-xs text-foreground-400">
            联系邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-topic" className="mb-1.5 block text-xs text-foreground-400">
          反馈类型
        </label>
        <select id="contact-topic" name="topic" required defaultValue="内容建议" className={`${inputClass} cursor-pointer`}>
          <option value="内容建议" className="bg-background-900">
            内容建议
          </option>
          <option value="影片信息纠错" className="bg-background-900">
            影片信息纠错
          </option>
          <option value="合作与投稿" className="bg-background-900">
            合作与投稿
          </option>
          <option value="其他问题" className="bg-background-900">
            其他问题
          </option>
        </select>
      </div>

      <div>
        <label htmlFor="contact-message" className="mb-1.5 block text-xs text-foreground-400">
          详细内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="请描述你遇到的问题或建议（最多 500 字）"
          className={`${inputClass} resize-none`}
        />
        <p className="mt-1 text-right text-[11px] text-foreground-500">{message.length}/500</p>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-foreground-50 transition-colors hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer"
        >
          {status === "submitting" ? "提交中…" : "提交反馈"}
        </button>

        {status === "success" ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-accent-400">
            <i className="ri-checkbox-circle-line" />
            提交成功，感谢你的反馈！
          </span>
        ) : null}

        {status === "error" ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-foreground-300">
            <i className="ri-error-warning-line text-accent-500" />
            {formError}
          </span>
        ) : null}
      </div>
    </form>
  );
}